import { store } from '../store';
import { handleUnauthorizedError } from './apiMiddleware';

export interface CatalogItem {
  id?: string;
  _id?: string;
  handle?: string;
  title?: string;
  bodyHtml?: string;
  vendor?: string;
  type?: string;
  tags?: string;
  Sku?: string;
  Grams?: number;
  Price?: number;
  Barcode?: number;
  EAN?: string;
  imageSrc?: string;
  Batch?: Array<{ Reference?: string; Quantity?: number; DLU?: string | Date }>;
  UseInstruction?: string;
  Contenance?: string;
  ShortDescription?: string;
  Disclaimer?: string;
  Faq?: string;
  FoundersChoice?: string;
  Inci?: string;
  Ingredients?: string[];
  Kitcomposition?: string;
  Preoccupations?: string[];
  customScoreYuka?: number;
  Typo?: string[];
  descriptorsSubtitle?: string;
  WeightUnit?: string;
  costPerItem?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;

  // Multi-warehouse stock management
  storageStock?: Array<{
    storageId: string;
    sellableQuantity: number; // Stock commercialisable
    reservedQuantity: number; // Stock réservé
    defectiveQuantity: number; // Stock défectueux
    totalQuantity: number; // Stock total (calculé ou stocké)
    storageName?: string;
    statusQuantities?: Array<{
      status: 'marketable' | 'reserved' | 'defective';
      quantity: number;
    }>;
    lots?: Array<{
      reference: string;
      expiryDate?: string | Date;
      statusQuantities: Array<{
        status: 'marketable' | 'reserved' | 'defective';
        quantity: number;
      }>;
      totalQuantity: number;
    }>;
  }>;

  // Compliance information (Cosmetics)
  compliance?: {
    cpnpNumber?: string;
    cpnpUrl?: string;
    dicUrl?: string;
    technicalSheetUrl?: string;
    responsiblePerson?: string;
    responsiblePersonAddress?: string;
    formulaCode?: string;
    paO?: string; // Period After Opening
    isVea?: boolean; // Voluntary Environmental Assessment
    certification?: string[];
  };

  // Distributor-specific pricing
  distributorPrices?: Array<{
    distributorId: string;
    distributorName?: string;
    price: number;
    currency?: string;
  }>;
}

export interface CatalogFieldMeta {
  key: string;
  csvField?: string | null;
  instance?: string;
  caster?: string | null;
}

export interface CatalogMappingResponse {
  source: string;
  fields: CatalogFieldMeta[];
}

const getAuthHeader = (): Record<string, string> => {
  const token = store.getState().user?.currentUser?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const API_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/catalog`;

const normalizeCatalogItem = (item: any): CatalogItem => ({
  ...item,
  id: item._id || item.id,
});

export const getCatalog = async (search?: string): Promise<CatalogItem[]> => {
  try {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await fetch(`${API_URL}${query}`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data.map(normalizeCatalogItem) : [];
  } catch (error) {
    console.error('Erreur lors de la récupération du catalogue:', error);
    throw error;
  }
};

export const getCatalogMapping = async (): Promise<CatalogMappingResponse> => {
  try {
    const response = await fetch(`${API_URL}/mapping`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération du mapping catalogue:', error);
    throw error;
  }
};

export const importCatalogCsv = async (payload: {
  csv: string;
  mapping: Record<string, string | null>;
}): Promise<{ inserted: number; updated: number; upserted: number; ignored: number }> => {
  try {
    const response = await fetch(`${API_URL}/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur lors de l\'import catalogue:', error);
    throw error;
  }
};

export const importStockReport = async (payload: {
  storageId: string;
  headers: string[];
  rows: unknown[][];
  fileName: string;
}): Promise<{
  updatedProducts: number;
  unchangedProducts: number;
  unmatched: Array<{
    row: number;
    productCode?: string;
    barcode?: string;
    quantity?: number;
    reason?: 'product' | 'status';
    status?: string;
  }>;
  logId?: string;
}> => {
  const response = await fetch(`${API_URL}/stock-import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });
  if (handleUnauthorizedError(response)) {
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }
  return response.json();
};

export const getCatalogItem = async (id: string): Promise<CatalogItem> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return normalizeCatalogItem(data);
  } catch (error) {
    console.error(`Erreur lors de la récupération du produit catalogue ${id}:`, error);
    throw error;
  }
};

export const createCatalogItem = async (payload: CatalogItem): Promise<CatalogItem> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return normalizeCatalogItem(data);
  } catch (error) {
    console.error('Erreur lors de la création du produit catalogue:', error);
    throw error;
  }
};

export const updateCatalogItem = async (
  id: string,
  payload: Partial<CatalogItem>
): Promise<CatalogItem> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return normalizeCatalogItem(data);
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du produit catalogue ${id}:`, error);
    throw error;
  }
};

export const deleteCatalogItem = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
  } catch (error) {
    console.error(`Erreur lors de la suppression du produit catalogue ${id}:`, error);
    throw error;
  }
};
